import { useState, useRef, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CloudArrowUp, X, Image, File, FilePdf, Spinner, Icon } from '@phosphor-icons/react';
import { uploadMultipleFiles } from '../lib/upload';

interface FileUploadProps {
  onUpload: (urls: string[]) => void;
  folder?: string;
  maxFiles?: number;
  accept?: string;
  className?: string;
}

export function FileUpload({ 
  onUpload, 
  folder = 'uploads', 
  maxFiles = 5, 
  accept = 'image/*,.pdf,.doc,.docx',
  className = ''
}: FileUploadProps) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const newFiles = Array.from(selectedFiles).slice(0, maxFiles - files.length);
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeUploadedUrl = (index: number) => {
    const newUrls = uploadedUrls.filter((_, i) => i !== index);
    setUploadedUrls(newUrls);
    onUpload(newUrls);
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      const urls = await uploadMultipleFiles(files, folder);
      const allUrls = [...uploadedUrls, ...urls];
      setUploadedUrls(allUrls);
      setFiles([]);
      onUpload(allUrls);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const getFileIcon = (file: File): Icon => {
    if (file.type.startsWith('image/')) return Image;
    if (file.type === 'application/pdf') return FilePdf;
    return File;
  };

  return (
    <div className={`space-y-4 ${className}`} data-testid="file-upload">
      {/* Drop zone */}
      <div
        className={`upload-zone ${dragging ? 'dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
      >
        <CloudArrowUp size={32} className="mx-auto text-slate-400 mb-2" weight="light" />
        <p className="text-sm text-slate-600">{t('upload_files')}</p>
        <p className="text-xs text-slate-400 mt-1">
          Max {maxFiles} filer
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleFiles(e.target.files)}
          className="hidden"
          data-testid="file-input"
        />
      </div>

      {/* Selected files */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => {
            const IconComponent = getFileIcon(file);
            return (
              <div
                key={`file-${file.name}-${file.size}-${index}`}
                className="flex items-center gap-3 p-2 border border-border rounded-md bg-white"
              >
                <IconComponent size={20} className="text-slate-400" />
                <span className="flex-1 text-sm text-slate-700 truncate">{file.name}</span>
                <button
                  onClick={() => removeFile(index)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
          
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors"
            data-testid="upload-button"
          >
            {uploading ? (
              <>
                <Spinner size={16} className="animate-spin" />
                {t('loading')}
              </>
            ) : (
              <>
                <CloudArrowUp size={16} />
                Ladda upp
              </>
            )}
          </button>
        </div>
      )}

      {/* Uploaded files */}
      {uploadedUrls.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-500 uppercase">Uppladdade filer</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {uploadedUrls.map((url, index) => (
              <div key={`uploaded-${index}-${url.slice(-20)}`} className="relative group">
                {url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <img
                    src={url}
                    alt=""
                    className="w-full h-20 object-cover rounded-md border border-border"
                  />
                ) : (
                  <div className="w-full h-20 flex items-center justify-center bg-slate-50 rounded-md border border-border">
                    <File size={24} className="text-slate-400" />
                  </div>
                )}
                <button
                  onClick={() => removeUploadedUrl(index)}
                  className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
